import React from 'react';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import cn from '../../utils/cn';
import Typography from '../Typography/Typography';

interface TooltipProps extends TooltipPrimitive.TooltipProps {
  className?: string;
  asChild?: boolean;
  position?: TooltipPrimitive.TooltipContentProps['side'];
  align?: TooltipPrimitive.TooltipContentProps['align'];
  children: React.ReactNode | string;
  content: React.ReactNode | string;
  portal?: boolean;
  enable?: boolean;
  arrow?: boolean;
}

function Tooltip({
  children,
  content,
  asChild,
  open,
  defaultOpen,
  align = 'center',
  position = 'top',
  className,
  portal = true,
  enable = true,
  arrow = true,
  onOpenChange,
  ...props
}: TooltipProps) {
  const tooltipContent =
    typeof content === 'string' ? (
      <Typography variant='caption-medium' color='white'>
        {content}
      </Typography>
    ) : (
      content
    );

  const WrapperPortal = portal ? TooltipPrimitive.Portal : React.Fragment;

  if (enable) {
    return (
      <TooltipPrimitive.Root
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
      >
        <TooltipPrimitive.Trigger asChild={asChild}>
          {children}
        </TooltipPrimitive.Trigger>
        <WrapperPortal>
          <TooltipPrimitive.Content
            sideOffset={5}
            side={position}
            align={align}
            className={cn(
              `z-tooltip max-w-52 rounded-md bg-secondary px-2.5 py-1.5
            text-white
            shadow-md
            will-change-[transform,opacity]
            data-[side=bottom]:animate-slide-up-and-fade
            data-[side=left]:animate-slide-right-and-fade
            data-[side=right]:animate-slide-left-and-fade
            data-[side=top]:animate-slide-down-and-fade
            `,
              className
            )}
            {...props}
          >
            {tooltipContent}
            {arrow && (
              <TooltipPrimitive.Arrow
                style={{ fill: 'black' }}
                width={12}
                height={6}
              />
            )}
          </TooltipPrimitive.Content>
        </WrapperPortal>
      </TooltipPrimitive.Root>
    );
  }

  return children;
}

const TooltipProvider = TooltipPrimitive.TooltipProvider;

export { TooltipProvider };

export default Tooltip;
